import { Controller } from "@hotwired/stimulus"

// Half-written words survive leaving the page, and the device. The body is
// kept in the browser's own storage under a key for the composer it belongs
// to - the page's composer, a letter to a particular post - and, a moment
// after the member stops typing, on the server under the same key (Draft),
// so a post begun on the phone is there at the desk. Coming back, the words
// are put back with a quiet note: this device's copy first, else the
// server's, which the page brought with it. Sharing the post, or starting
// over, forgets both. Nobody reads a draft.
export default class extends Controller {
  static targets = ["field", "note"]
  static values = { key: String, url: String, saved: String }

  connect() {
    // A composer drawn with words already in it - a post the check would not
    // take, handed back to be looked at again - keeps them as a draft too. One
    // drawn empty gets back whatever was kept for it.
    if (this.fieldTarget.value.trim().length > 0) this.changed()
    else this.restore()
  }

  disconnect() {
    clearTimeout(this.timer)
    clearTimeout(this.remoteTimer)
  }

  changed() {
    if (this.onTheirWay) return

    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.save(), 400)
    clearTimeout(this.remoteTimer)
    this.remoteTimer = setTimeout(() => this.send(), 1500)
  }

  // The words are on their way. The draft is over now, not when the answer
  // comes back: the save that follows the typing by a second and a half was
  // landing after the post it had become, and a member who wrote a line and
  // sent it found it waiting in the box they had just emptied. Nothing more is
  // kept for these words unless they are handed back.
  going() {
    this.onTheirWay = true
    this.forget()
  }

  // Turbo tells us how the form went. Refused, and the words are a draft
  // again - they are still in the box, and the composer drawn in their place
  // keeps them (connect).
  submitted(event) {
    if (event.detail?.success === false) this.onTheirWay = false
    else this.forget()
  }

  discard() {
    this.fieldTarget.value = ""
    this.fieldTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.forget()
  }

  // --- storage -------------------------------------------------------------

  restore() {
    const field = this.fieldTarget
    if (field.value.trim().length > 0) return
    const saved = this.read() || this.savedValue
    if (!saved) return

    field.value = saved
    field.dispatchEvent(new Event("input", { bubbles: true }))
    if (this.hasNoteTarget) this.noteTarget.hidden = false
  }

  save() {
    const text = this.fieldTarget.value
    if (text.trim().length === 0) return this.forget()
    try { localStorage.setItem(this.storageKey, text) } catch { /* private mode, full: the draft is simply not kept */ }
  }

  forget() {
    clearTimeout(this.timer)
    clearTimeout(this.remoteTimer)
    try { localStorage.removeItem(this.storageKey) } catch { /* nothing to forget */ }
    if (this.hasNoteTarget) this.noteTarget.hidden = true
    this.send("")
  }

  read() {
    try { return localStorage.getItem(this.storageKey) } catch { return null }
  }

  readComposition() {
    try { return localStorage.getItem(`substance:composition:${this.keyValue || "post"}`) } catch { return null }
  }

  // The server's copy, written or taken away. Quietly: a failure here costs
  // nothing but the copy, and the page never waits on it.
  send(text = this.fieldTarget.value) {
    if (!this.hasUrlValue || !this.urlValue) return
    if (text === this.sent) return
    this.sent = text

    const empty = text.trim().length === 0
    const body = new FormData()
    if (!empty) {
      body.append("body", text)
      // How the words arrived so far, from the composition controller's copy
      // beside this one, so the other device knows they were typed.
      const composition = this.readComposition()
      if (composition) body.append("composition", composition)
    }
    fetch(this.urlValue, {
      method: empty ? "DELETE" : "PUT",
      headers: { "X-CSRF-Token": this.csrf, "Accept": "text/plain" },
      body: empty ? null : body,
      credentials: "same-origin",
      keepalive: true
    }).catch(() => { this.sent = undefined })
  }

  get csrf() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }

  get storageKey() {
    return `substance:draft:${this.keyValue || "post"}`
  }
}
